import React, { useState, useEffect } from 'react';
import { Bell, Smartphone, Clock } from 'lucide-react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { NotificationPreferences as NotifPrefs } from '../types';

interface PrefsProps {
  userId: string | null;
}

const NotificationPreferences: React.FC<PrefsProps> = ({ userId }) => {
  const [prefs, setPrefs] = useState<NotifPrefs>({
    userId: userId || '',
    likes: true,
    comments: true,
    memberJoined: true,
    birthdayReminders: true,
    eventReminders: true,
    adminAlerts: true,
    emailNotifications: false,
    pushNotifications: true,
    quietHours: { start: '22:00', end: '08:00' }
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Load preferences
  useEffect(() => {
    if (!userId) return;

    const loadPrefs = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'notificationPreferences', userId));
        if (docSnap.exists()) {
          setPrefs(docSnap.data() as NotifPrefs);
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
      }
    };
    loadPrefs();
  }, [userId]);

  const handleSave = async () => {
    if (!userId) {
      setMessage('✗ User not authenticated');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const prefsToSave = {
        userId,
        likes: prefs.likes,
        comments: prefs.comments,
        memberJoined: prefs.memberJoined,
        birthdayReminders: prefs.birthdayReminders,
        eventReminders: prefs.eventReminders,
        adminAlerts: true,
        pushNotifications: prefs.pushNotifications,
        quietHours: prefs.quietHours
      };
      await setDoc(doc(db, 'notificationPreferences', userId), prefsToSave, { merge: true });
      setMessage('✓ Preferences saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Failed to save:', error);
      const errorMsg = error?.message || 'Unknown error';
      setMessage(`✗ Failed: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const togglePref = (key: keyof NotifPrefs) => {
    // Admin alerts are mandatory and cannot be disabled
    if (key === 'adminAlerts') return;
    if (typeof prefs[key] === 'boolean') {
      setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-sm">
      <h2 className="text-2xl font-black text-gray-900 mb-8 flex items-center gap-2">
        <Bell className="w-6 h-6 text-indigo-600" /> Notification Settings
      </h2>

      <div className="space-y-6">
        {/* In-App Notifications */}
        <div className="pb-6 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="w-4 h-4 text-indigo-600" /> In-App Notifications
          </h3>
          <div className="space-y-3">
            {[
              { key: 'likes' as const, label: '❤️ Likes on your posts' },
              { key: 'comments' as const, label: '💬 Comments on your posts' },
              { key: 'memberJoined' as const, label: '👋 New family members' },
              { key: 'birthdayReminders' as const, label: '🎂 Birthday reminders' },
              { key: 'eventReminders' as const, label: '📅 Event reminders' },
              { key: 'adminAlerts' as const, label: '🚨 Admin alerts (mandatory)' },
            ].map(({ key, label }) => (
              <label key={key} className={`flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors ${key === 'adminAlerts' ? 'opacity-60' : ''}`}>
                <input
                  type="checkbox"
                  checked={prefs[key] as boolean}
                  onChange={() => togglePref(key)}
                  disabled={key === 'adminAlerts'}
                  className={`w-5 h-5 accent-indigo-600 ${key === 'adminAlerts' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                />
                <span className="text-gray-700 font-medium">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Delivery Methods */}
        <div className="pb-6 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 mb-4">Delivery Methods</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors">
              <input
                type="checkbox"
                checked={prefs.pushNotifications}
                onChange={() => togglePref('pushNotifications')}
                className="w-5 h-5 accent-indigo-600 cursor-pointer"
              />
              <span className="text-gray-700 font-medium flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-indigo-600" /> Push Notifications
              </span>
            </label>
          </div>
        </div>

        {/* Quiet Hours */}
        <div>
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-purple-600" /> Quiet Hours
          </h3>
          <p className="text-sm text-gray-600 mb-4">Don't notify me between these times</p>
          <div className="flex gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div className="flex-1">
              <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Start Time</label>
              <input
                type="time"
                value={prefs.quietHours?.start || '22:00'}
                onChange={(e) => setPrefs(prev => ({
                  ...prev,
                  quietHours: { ...(prev.quietHours || { start: '22:00', end: '08:00' }), start: e.target.value }
                }))}
                className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">End Time</label>
              <input
                type="time"
                value={prefs.quietHours?.end || '08:00'}
                onChange={(e) => setPrefs(prev => ({
                  ...prev,
                  quietHours: { ...(prev.quietHours || { start: '22:00', end: '08:00' }), end: e.target.value }
                }))}
                className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            You won't receive notifications between {prefs.quietHours?.start} and {prefs.quietHours?.end}
          </p>
        </div>
      </div>

      {/* SAVE BUTTON */}
      <div className="mt-8 space-y-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-lg shadow-indigo-200 active:scale-95"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
        {message && (
          <p className={`text-sm text-center font-bold ${message.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default NotificationPreferences;
